import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Copy, ChevronDown, ChevronUp } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface CodeBlockProps {
  code: string
  /** Number of lines to show before collapsing. Default: 6 */
  collapsedLines?: number
  className?: string
}

export function CodeBlock({ code, collapsedLines = 6, className }: CodeBlockProps) {
  const { t } = useTranslation('common')
  const [expanded, setExpanded] = useState(false)
  const [copied, setCopied] = useState(false)

  const lineCount = code.split('\n').length
  const canCollapse = lineCount > collapsedLines

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code)
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    } catch {
      toast.error(t('common.copyFailed'))
    }
  }

  // ~1.6rem per line + 2rem for top/bottom padding
  const collapsedMaxHeight = `${collapsedLines * 1.6 + 2}rem`

  return (
    <div className={cn('relative rounded-xl border border-border/50 bg-background', className)}>
      {/* Code area — clipped when collapsed */}
      <div
        className={cn('relative', !expanded && canCollapse && 'overflow-hidden')}
        style={!expanded && canCollapse ? { maxHeight: collapsedMaxHeight } : undefined}
      >
        <pre className="p-4 pr-16 text-sm font-mono whitespace-pre-wrap break-words leading-relaxed">
          <code>{code}</code>
        </pre>

        {/* Bottom fade — only when collapsed */}
        {!expanded && canCollapse && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-background to-transparent" />
        )}
      </div>

      {/* Copy button — anchored top-right of outer container */}
      <Button
        variant={copied ? 'default' : 'outline'}
        size="sm"
        className="absolute right-3 top-3 z-10 gap-1.5"
        onClick={handleCopy}
      >
        {copied ? (
          <>
            <Check className="h-3.5 w-3.5" />
            {t('common.copied')}
          </>
        ) : (
          <>
            <Copy className="h-3.5 w-3.5" />
            {t('common.copy')}
          </>
        )}
      </Button>

      {/* Expand / collapse toggle */}
      {canCollapse && (
        <div className="flex justify-center border-t border-border/40 py-1.5">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? (
              <>
                {t('common.showLess')}
                <ChevronUp className="h-3.5 w-3.5" />
              </>
            ) : (
              <>
                {t('common.showMore')}
                <ChevronDown className="h-3.5 w-3.5" />
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  )
}
